import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError } from "@/contexts/AuthContext";
import {
  ArrowLeft, Package, Plus, Pencil, Trash2, Upload, Download, Save,
  Truck, PackageCheck, Clock, Loader2, X, Mail, FileText,
} from "lucide-react";

const CATEGORIES = [
  { value: "brochure",         label: "Brochure" },
  { value: "rate_card",        label: "Rate Card" },
  { value: "flyer",            label: "Flyer" },
  { value: "poster",           label: "Poster" },
  { value: "table_topper",     label: "Table Topper" },
  { value: "desktop_laminate", label: "Desktop Laminate" },
  { value: "banner",           label: "Banner" },
  { value: "business_card",    label: "Business Card" },
  { value: "other",            label: "Other" },
];
const PROGRAMS = [
  { value: "general",     label: "General / All programs" },
  { value: "essentials",  label: "Pre-Owned VSC (Essentials)" },
  { value: "franchise",   label: "Franchise VSC" },
  { value: "ancillary",   label: "4n1 Ancillary Bundle" },
  { value: "wear_guard",  label: "Wear Guard" },
  { value: "gap",         label: "GAP" },
  { value: "reinsurance", label: "Reinsurance" },
];
const VERTICALS = [
  { value: "", label: "None / generic" },
  { value: "automotive", label: "Automotive" },
  { value: "rv", label: "RV" },
  { value: "powersports", label: "Powersports" },
  { value: "marine", label: "Marine" },
];

const fileSizeLabel = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};
const STATUS_STYLES = {
  new:        { icon: Clock,         cls: "bg-blue-50 text-blue-700 border-blue-200" },
  processing: { icon: Loader2,       cls: "bg-amber-50 text-amber-700 border-amber-200" },
  shipped:    { icon: Truck,         cls: "bg-purple-50 text-purple-700 border-purple-200" },
  delivered:  { icon: PackageCheck,  cls: "bg-green-50 text-green-700 border-green-200" },
  cancelled:  { icon: X,             cls: "bg-red-50 text-red-700 border-red-200" },
};
const NEXT_STATUS = { new: "processing", processing: "shipped", shipped: "delivered" };
const NEXT_LABEL = { new: "Mark processing", processing: "Mark shipped", shipped: "Mark delivered" };

export default function AdminMarketing() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const deepLinkedProgram = params.get("program");
  const initialTab = deepLinkedProgram ? "materials" : "orders";
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6" data-testid="admin-marketing-page">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-[#6C757D] hover:text-[#0B132B] mb-2">
          <ArrowLeft size={14} /> Back
        </Link>
        <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold flex items-center gap-2">
          <Package size={12} /> Admin · Marketing
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-black text-[#0B132B] tracking-tight leading-none mt-1 mb-5">
          Manage marketing materials
        </h1>

        <Tabs defaultValue={initialTab} className="w-full">
          <TabsList className="rounded-sm bg-white border border-[#E9ECEF] p-1">
            <TabsTrigger value="orders" className="rounded-sm data-[state=active]:bg-[#0B132B] data-[state=active]:text-white" data-testid="admin-tab-orders">Order queue</TabsTrigger>
            <TabsTrigger value="materials" className="rounded-sm data-[state=active]:bg-[#0B132B] data-[state=active]:text-white" data-testid="admin-tab-materials">Materials catalog</TabsTrigger>
            <TabsTrigger value="settings" className="rounded-sm data-[state=active]:bg-[#0B132B] data-[state=active]:text-white" data-testid="admin-tab-settings">Fulfillment settings</TabsTrigger>
          </TabsList>
          <TabsContent value="orders" className="mt-4"><OrdersTab /></TabsContent>
          <TabsContent value="materials" className="mt-4"><MaterialsTab initialProgram={deepLinkedProgram} /></TabsContent>
          <TabsContent value="settings" className="mt-4"><SettingsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ============================================================================
// Orders tab
// ============================================================================
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter === "all" ? {} : { status: statusFilter };
      const { data } = await api.get("/admin/marketing/orders", { params });
      setOrders(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);

  const advance = async (o, nextStatus, trackingNumber, trackingUrl, carrier) => {
    try {
      await api.patch(`/admin/marketing/orders/${o.id}/status`, {
        status: nextStatus,
        tracking_number: trackingNumber || null,
        tracking_url: trackingUrl || null,
        carrier: carrier || null,
      });
      toast.success(`Order ${o.order_number} → ${nextStatus}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs font-bold uppercase tracking-wider text-[#6C757D]">Filter</span>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-[180px] rounded-sm" data-testid="admin-orders-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="shipped">Shipped</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-[#6C757D]">{orders.length} orders</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#6C757D]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>
      ) : orders.length === 0 ? (
        <Card className="p-10 text-center border-dashed border-[#E9ECEF]"><Package size={32} className="mx-auto text-[#6C757D] mb-2" /><div className="font-bold text-[#0B132B]">No orders</div></Card>
      ) : (
        <div className="space-y-3" data-testid="admin-orders-list">
          {orders.map((o) => <AdminOrderCard key={o.id} order={o} onAdvance={advance} />)}
        </div>
      )}
    </div>
  );
}

function AdminOrderCard({ order, onAdvance }) {
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [trackingUrl, setTrackingUrl] = useState(order.tracking_url || "");
  const [carrier, setCarrier] = useState(order.carrier || "UPS");
  const [showShip, setShowShip] = useState(false);
  const { icon: Icon, cls } = STATUS_STYLES[order.status] || STATUS_STYLES.new;
  const nextStatus = NEXT_STATUS[order.status];

  return (
    <Card className="p-4 border-[#E9ECEF] rounded-sm bg-white" data-testid={`admin-order-${order.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#D4AF37]">Order</div>
          <div className="font-bold text-lg text-[#0B132B] font-mono">{order.order_number}</div>
          <div className="text-xs text-[#6C757D] mt-1">
            <b>{order.agent_name || order.agent_email}</b> · {order.agent_email}
            {order.agent_phone && <> · {order.agent_phone}</>}
            {order.agency_name && <> · {order.agency_name}</>}
          </div>
          <div className="text-xs text-[#6C757D] mt-0.5">{new Date(order.created_at).toLocaleString()}</div>
        </div>
        <Badge className={`rounded-sm border ${cls} inline-flex items-center gap-1.5 px-3 py-1`} data-testid={`admin-order-status-${order.id}`}>
          <Icon size={12} /> {order.status_label}
        </Badge>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[11px] font-bold uppercase text-[#6C757D] mb-1">Items</div>
          <ul className="space-y-1">
            {order.items.map((it, idx) => (
              <li key={idx} className="text-sm text-[#0B132B]"><b>{it.qty}×</b> {it.title}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-[11px] font-bold uppercase text-[#6C757D] mb-1">Ship to</div>
          <div className="text-sm text-[#0B132B] leading-relaxed">
            {order.ship_to?.name}{order.ship_to?.company && <><br />{order.ship_to.company}</>}<br />
            {order.ship_to?.address1}{order.ship_to?.address2 && <>, {order.ship_to.address2}</>}<br />
            {order.ship_to?.city}, {order.ship_to?.state} {order.ship_to?.zip}
            {order.ship_to?.phone && <><br />☎ {order.ship_to.phone}</>}
          </div>
        </div>
      </div>

      {order.notes && (
        <div className="mt-3 pt-3 border-t border-[#F1F3F5] text-sm">
          <div className="text-[11px] font-bold uppercase text-[#6C757D] mb-1">Notes</div>
          <div className="text-sm text-[#0B132B] italic">{order.notes}</div>
        </div>
      )}

      <div className="mt-4 pt-3 border-t border-[#F1F3F5] flex items-center gap-2 flex-wrap">
        {order.status !== "cancelled" && order.status !== "delivered" && nextStatus && (
          <>
            {nextStatus === "shipped" ? (
              <Dialog open={showShip} onOpenChange={setShowShip}>
                <DialogTrigger asChild>
                  <Button size="sm" className="rounded-sm bg-[#0B132B] text-white hover:bg-[#0B132B]/90" data-testid={`admin-order-ship-${order.id}`}>
                    <Truck size={12} className="mr-1.5" /> {NEXT_LABEL[order.status]}
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid={`admin-order-ship-dialog-${order.id}`}>
                  <DialogHeader><DialogTitle>Mark {order.order_number} as shipped</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs">Carrier</Label>
                      <Select value={carrier} onValueChange={setCarrier}>
                        <SelectTrigger className="rounded-sm h-9" data-testid={`admin-carrier-${order.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UPS">UPS</SelectItem>
                          <SelectItem value="FedEx">FedEx</SelectItem>
                          <SelectItem value="USPS">USPS</SelectItem>
                          <SelectItem value="DHL">DHL</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Tracking number (optional)</Label>
                      <Input value={tracking} onChange={(e) => setTracking(e.target.value)} className="rounded-sm h-9" data-testid={`admin-tracking-${order.id}`} />
                    </div>
                    <div>
                      <Label className="text-xs">Tracking URL (optional)</Label>
                      <Input value={trackingUrl} onChange={(e) => setTrackingUrl(e.target.value)} className="rounded-sm h-9" data-testid={`admin-tracking-url-${order.id}`} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button size="sm" className="bg-[#0B132B] text-white" onClick={() => { onAdvance(order, "shipped", tracking, trackingUrl, carrier); setShowShip(false); }} data-testid={`admin-order-ship-confirm-${order.id}`}>
                      <Truck size={12} className="mr-1.5" /> Confirm ship
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            ) : (
              <Button size="sm" className="rounded-sm bg-[#0B132B] text-white hover:bg-[#0B132B]/90" onClick={() => onAdvance(order, nextStatus)} data-testid={`admin-order-advance-${order.id}`}>
                {NEXT_LABEL[order.status]}
              </Button>
            )}
          </>
        )}
        {order.status !== "cancelled" && order.status !== "delivered" && (
          <Button size="sm" variant="outline" className="rounded-sm border-red-300 text-red-700 hover:bg-red-50" onClick={() => onAdvance(order, "cancelled")} data-testid={`admin-order-cancel-${order.id}`}>
            <X size={12} className="mr-1.5" /> Cancel
          </Button>
        )}
        {order.tracking_number && (
          <span className="text-xs text-[#6C757D] ml-2">
            {order.carrier && <><b className="text-[#0B132B]">{order.carrier}</b> · </>}
            Tracking: {order.tracking_url ? <a href={order.tracking_url} target="_blank" rel="noreferrer" className="font-mono font-bold text-[#0B132B] underline">{order.tracking_number}</a> : <span className="font-mono font-bold text-[#0B132B]">{order.tracking_number}</span>}
          </span>
        )}
      </div>
    </Card>
  );
}

// ============================================================================
// Materials tab
// ============================================================================
function MaterialsTab({ initialProgram }) {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [programFilter, setProgramFilter] = useState(initialProgram || "all");

  const load = async () => {
    setLoading(true);
    try {
      const url = programFilter && programFilter !== "all"
        ? `/admin/marketing/materials?program=${encodeURIComponent(programFilter)}`
        : "/admin/marketing/materials";
      const { data } = await api.get(url);
      setMaterials(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [programFilter]);

  const openNew = () => {
    // Preseed the program from the current filter so a Rate-page deep-link
    // ("Manage materials · Wear Guard") drops the admin straight into a
    // pre-tagged new-material form.
    setEditing(programFilter && programFilter !== "all" ? { _preseedProgram: programFilter } : null);
    setDialogOpen(true);
  };
  const openEdit = (m) => { setEditing(m); setDialogOpen(true); };

  const onDelete = async (m) => {
    if (!window.confirm(`Delete "${m.title}"?`)) return;
    try { await api.delete(`/admin/marketing/materials/${m.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const programCount = (p) => materials.filter(m => (m.program || "general") === p).length;
  const filteredForBadge = programFilter === "all"
    ? materials
    : materials.filter(m => (m.program || "general") === programFilter);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold uppercase tracking-wider text-[#6C757D]">Program</span>
          <Select value={programFilter} onValueChange={setProgramFilter}>
            <SelectTrigger className="h-9 w-[240px] rounded-sm bg-white" data-testid="admin-material-program-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All programs</SelectItem>
              {PROGRAMS.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-sm text-[#6C757D]" data-testid="admin-material-count">{filteredForBadge.length} materials</span>
        </div>
        <Button size="sm" onClick={openNew} className="rounded-sm bg-[#0B132B] text-white" data-testid="admin-material-new-btn">
          <Plus size={12} className="mr-1.5" /> New material
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#6C757D]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>
      ) : filteredForBadge.length === 0 ? (
        <Card className="p-10 text-center border-dashed border-[#E9ECEF]"><Package size={32} className="mx-auto text-[#6C757D] mb-2" /><div className="font-bold text-[#0B132B]">No materials for this program yet</div><div className="text-xs text-[#6C757D] mt-1">Click New material to upload the first brochure, table topper, poster, or desktop laminate.</div></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-materials-grid">
          {filteredForBadge.map((m) => (
            <Card key={m.id} className="p-4 border-[#E9ECEF] rounded-sm bg-white flex flex-col" data-testid={`admin-material-${m.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#D4AF37]">
                    {CATEGORIES.find(c => c.value === m.category)?.label || m.category}
                    {m.vertical_slug && ` · ${VERTICALS.find(v => v.value === m.vertical_slug)?.label || m.vertical_slug}`}
                  </div>
                  <div className="font-bold text-[#0B132B] leading-snug mt-1">{m.title}</div>
                  <div className="text-[10px] mt-1">
                    <span className="px-1.5 py-0.5 rounded-sm border border-[#0B132B]/15 bg-[#F8F9FA] text-[#0B132B] font-bold uppercase tracking-wider" data-testid={`admin-material-program-${m.id}`}>
                      {PROGRAMS.find(p => p.value === (m.program || "general"))?.label || m.program}
                    </span>
                  </div>
                </div>
                {!m.active && <Badge className="bg-gray-100 text-gray-600 rounded-sm text-[10px]">Inactive</Badge>}
              </div>
              {m.description && <div className="text-xs text-[#6C757D] mt-2">{m.description}</div>}
              <div className="text-[11px] text-[#6C757D] mt-2">
                {m.has_file ? <><FileText size={10} className="inline mr-1" />{m.filename} · {fileSizeLabel(m.file_size)}</> : <span className="italic">No file uploaded</span>}
              </div>
              <div className="flex-1" />
              <div className="flex items-center gap-1 mt-3 pt-3 border-t border-[#F1F3F5]">
                <Button size="sm" variant="outline" className="rounded-sm h-8 flex-1" onClick={() => openEdit(m)} data-testid={`admin-material-edit-${m.id}`}>
                  <Pencil size={11} className="mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" className="rounded-sm h-8 border-red-300 text-red-700 hover:bg-red-50" onClick={() => onDelete(m)} data-testid={`admin-material-delete-${m.id}`}>
                  <Trash2 size={11} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <MaterialEditDialog open={dialogOpen} onOpenChange={setDialogOpen} material={editing?._preseedProgram ? null : editing} preseedProgram={editing?._preseedProgram} onSaved={load} />
    </div>
  );
}

function MaterialEditDialog({ open, onOpenChange, material, preseedProgram, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", category: "brochure", program: "general", vertical_slug: "", active: true });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Track the current material inside the dialog so upload-in-place reflects
  // the new file badge without closing and reopening.
  const [current, setCurrent] = useState(material);
  const fileRef = useRef(null);

  useEffect(() => {
    setCurrent(material);
    if (material) setForm({
      title: material.title || "", description: material.description || "",
      category: material.category || "brochure",
      program: material.program || "general",
      vertical_slug: material.vertical_slug || "",
      active: material.active !== false,
    });
    else setForm({ title: "", description: "", category: "brochure", program: preseedProgram || "general", vertical_slug: "", active: true });
  }, [material, preseedProgram, open]);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const body = { ...form, vertical_slug: form.vertical_slug || null };
      let saved;
      if (current) {
        const { data } = await api.patch(`/admin/marketing/materials/${current.id}`, body);
        saved = data;
      } else {
        const { data } = await api.post(`/admin/marketing/materials`, body);
        saved = data;
      }
      toast.success(current ? "Updated" : "Created");
      setCurrent(saved);
      onSaved();
      // Keep the dialog open on a freshly-created material so admin can
      // immediately upload the PDF; close on subsequent saves/edits.
      if (current) onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const uploadFile = async (file) => {
    if (!current) { toast.error("Save the material first, then upload the file."); return; }
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/marketing/materials/${current.id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCurrent(data);
      toast.success("File uploaded");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="admin-material-dialog">
        <DialogHeader><DialogTitle>{current ? "Edit material" : "New material"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm h-9" data-testid="admin-material-title" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="rounded-sm text-sm" data-testid="admin-material-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Program *</Label>
              <Select value={form.program} onValueChange={(v) => setForm({ ...form, program: v })}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="admin-material-program"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROGRAMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="h-9 rounded-sm" data-testid="admin-material-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Vertical (optional)</Label>
            <Select value={form.vertical_slug || "__none__"} onValueChange={(v) => setForm({ ...form, vertical_slug: v === "__none__" ? "" : v })}>
              <SelectTrigger className="h-9 rounded-sm" data-testid="admin-material-vertical"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None / generic</SelectItem>
                {VERTICALS.filter(v => v.value).map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} id="mat-active" data-testid="admin-material-active" />
            <Label htmlFor="mat-active" className="text-xs">Active (visible to agents)</Label>
          </div>

          {current && (
            <div className="pt-3 border-t border-[#E9ECEF]">
              <div className="text-xs uppercase tracking-wider font-bold text-[#6C757D] mb-2">File (PDF)</div>
              <div className="flex items-center gap-2 flex-wrap">
                <input ref={fileRef} type="file" accept="application/pdf,image/*" onChange={(e) => uploadFile(e.target.files?.[0])}
                  className="hidden" data-testid="admin-material-file-input" />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-sm" data-testid="admin-material-file-btn">
                  {uploading ? <><Loader2 className="animate-spin mr-1.5" size={12} /> Uploading…</> : <><Upload size={12} className="mr-1.5" /> {current.has_file ? "Replace file" : "Upload file"}</>}
                </Button>
                {current.has_file && (
                  <span className="text-xs text-[#6C757D] inline-flex items-center gap-1" data-testid="admin-material-file-badge">
                    <FileText size={10} />
                    <b className="text-[#0B132B]">{current.filename}</b>
                    <span>· {fileSizeLabel(current.file_size)}</span>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button size="sm" onClick={save} disabled={saving} className="bg-[#0B132B] text-white" data-testid="admin-material-save-btn">
            {saving ? <><Loader2 className="animate-spin mr-1.5" size={12} /> Saving…</> : <><Save size={12} className="mr-1.5" /> {current ? "Save changes" : "Create material"}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Settings tab (fulfillment email)
// ============================================================================
function SettingsTab() {
  const [email, setEmail] = useState("");
  const [initial, setInitial] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings/fulfillment");
        setEmail(data.fulfillment_email || ""); setInitial(data.fulfillment_email || "");
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail));
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/fulfillment", { fulfillment_email: email });
      toast.success("Fulfillment email updated");
      setInitial(email);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-6 border-[#E9ECEF] rounded-sm bg-white max-w-xl" data-testid="admin-settings-tab">
      <div className="text-[11px] uppercase tracking-wider font-bold text-[#D4AF37] flex items-center gap-1.5 mb-1">
        <Mail size={12} /> Fulfillment inbox
      </div>
      <h3 className="font-bold text-lg text-[#0B132B] mb-1">Where should new orders be emailed?</h3>
      <p className="text-sm text-[#6C757D] mb-4">Every new marketing-material order is emailed here with agent contact info, ship-to, and item list.</p>
      {loading ? (
        <div className="text-[#6C757D]"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>
      ) : (
        <div className="flex items-center gap-2">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="rounded-sm h-10 flex-1" placeholder="fulfillment@example.com" data-testid="admin-fulfillment-email" />
          <Button size="sm" onClick={save} disabled={saving || email === initial || !email}
            className="rounded-sm bg-[#0B132B] text-white h-10" data-testid="admin-fulfillment-save-btn">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <><Save size={12} className="mr-1.5" /> Save</>}
          </Button>
        </div>
      )}
    </Card>
  );
}
